import type { AthenaAuthBindings, AthenaAuthClientConfig } from './auth/types.ts'
import {
  createInternalClientCore,
  createInternalClientView,
  type AthenaFromOptions,
  type InternalAthenaClientCore,
  type InternalAthenaClient,
  type AthenaQueryTraceOptions,
  type AthenaRequestOptions,
  type AthenaRequestResponse,
  type AthenaResult,
  type RpcQueryBuilder,
  type TableQueryBuilder,
} from './client.ts'
import type { AthenaChatModule, AthenaChatWebSocketFactory } from './chat/types.ts'
import type { AthenaDbModule } from './db/module.ts'
import type {
  AthenaGatewayCallOptions,
  AthenaGatewayConnectionOptions,
  AthenaGatewayConnectionResult,
  AthenaJsonObject,
  AthenaRpcCallOptions,
  BackendConfig,
  BackendType,
} from './gateway/types.ts'
import type {
  AthenaClientModelsInput,
  AthenaClientModelForTableName,
  AthenaClientTableName,
  AthenaModelTarget,
  InsertOf,
  RowOf,
  UpdateOf,
} from './schema/types.ts'
import type {
  AthenaStorageClientConfig,
  AthenaStorageDirectUploadConfig,
  AthenaStorageModule,
} from './storage/module.ts'
import {
  createBillingModule,
  type AthenaBillingClientConfig,
  type AthenaBillingModule,
} from './billing/module.ts'
import {
  AthenaConfigurationError,
  type AthenaService,
} from './config/errors.ts'
import { mergeAthenaRequestContexts } from './context/merge.ts'

type MaybePromise<T> = T | Promise<T>
type ResolvedModels<TModels> = TModels extends AthenaClientModelsInput ? TModels : never

const ENV_URL_KEYS = ['ATHENA_URL', 'NEXT_PUBLIC_ATHENA_URL'] as const
const ENV_DB_URL_KEYS = ['ATHENA_DB_URL', 'ATHENA_GATEWAY_URL', 'NEXT_PUBLIC_ATHENA_DB_API_URL'] as const
const ENV_AUTH_URL_KEYS = ['ATHENA_AUTH_URL', 'NEXT_PUBLIC_ATHENA_AUTH_URL'] as const
const ENV_CHAT_URL_KEYS = ['ATHENA_CHAT_URL', 'NEXT_PUBLIC_ATHENA_CHAT_URL'] as const
const ENV_CHAT_WS_URL_KEYS = ['ATHENA_CHAT_WS_URL', 'NEXT_PUBLIC_ATHENA_CHAT_WS_URL'] as const
const ENV_STORAGE_URL_KEYS = ['ATHENA_STORAGE_URL', 'NEXT_PUBLIC_ATHENA_STORAGE_URL'] as const
const ENV_KEY_KEYS = ['ATHENA_API_KEY', 'NEXT_PUBLIC_ATHENA_API_KEY', 'ATHENA_GATEWAY_API_KEY', 'X_API_KEY'] as const
const ENV_CLIENT_KEYS = ['ATHENA_CLIENT', 'NEXT_PUBLIC_ATHENA_CLIENT'] as const
const MISSING_DB_SENTINEL = 'https://athena.invalid/db'

export {
  AthenaConfigurationError,
  type AthenaConfigurationErrorCode,
  type AthenaService,
} from './config/errors.ts'

export interface AthenaRequestContext {
  userId?: string | null
  organizationId?: string | null
  headers?: Record<string, string>
  cookie?: string | null
  bearerToken?: string | null
  sessionToken?: string | null
  forceNoCache?: boolean
}

export type AthenaRequestContextProvider = () => MaybePromise<AthenaRequestContext | undefined>

export interface AthenaDbConfig {
  url?: string | null
  pgUri?: string | null
  jdbcUrl?: string | null
}

export interface AthenaAuthConfig
  extends Omit<
    AthenaAuthClientConfig,
    'baseUrl' | 'apiKey' | 'bearerToken' | 'cookie' | 'sessionToken'
  > {
  url?: string | null
}

export interface AthenaChatConfig {
  url?: string | null
  wsUrl?: string | null
  webSocketFactory?: AthenaChatWebSocketFactory | null
}

export interface AthenaStorageConfig
  extends Omit<AthenaStorageClientConfig, 'baseUrl' | 'directUpload'> {
  url?: string | null
  directUpload?: AthenaStorageDirectUploadConfig
}

export interface AthenaBillingConfig
  extends Omit<AthenaBillingClientConfig, 'baseUrl' | 'apiKey' | 'client' | 'fetchImpl'> {
  /** Optional override; defaults to the unified/root or db Athena URL. */
  url?: string | null
}

export interface AthenaClientConfig<
  TModels extends AthenaClientModelsInput | undefined = undefined,
> {
  url?: string | null
  key?: string | null
  client?: string | null
  backend?: BackendConfig | BackendType
  headers?: Record<string, string>
  models?: TModels
  env?: Record<string, string | undefined>
  context?: AthenaRequestContext | AthenaRequestContextProvider
  db?: AthenaDbConfig
  auth?: AthenaAuthConfig
  chat?: AthenaChatConfig
  storage?: AthenaStorageConfig
  billing?: AthenaBillingConfig
  retryReads?: boolean
  traceQueries?: boolean | AthenaQueryTraceOptions
  debugAst?: boolean
  findManyAst?: boolean
}

type V3TableBuilder<
  Row,
  Insert = Partial<Row>,
  Update = Partial<Insert>,
  Result = unknown,
> = TableQueryBuilder<Row, Insert, Update, Result>

export interface AthenaClient<
  TModels extends AthenaClientModelsInput | undefined = AthenaClientModelsInput | undefined,
> {
  readonly db: AthenaDbModule<ResolvedModels<TModels>>
  readonly auth: AthenaAuthBindings
  readonly chat: AthenaChatModule
  readonly storage: AthenaStorageModule
  readonly billing: AthenaBillingModule
  from<TModel extends AthenaModelTarget>(
    model: TModel,
  ): V3TableBuilder<RowOf<TModel>, InsertOf<TModel>, UpdateOf<TModel>>
  from<TTableName extends AthenaClientTableName<ResolvedModels<TModels>>>(
    table: TTableName,
    options?: AthenaFromOptions,
  ): V3TableBuilder<
    RowOf<AthenaClientModelForTableName<ResolvedModels<TModels>, TTableName>>,
    InsertOf<AthenaClientModelForTableName<ResolvedModels<TModels>, TTableName>>,
    UpdateOf<AthenaClientModelForTableName<ResolvedModels<TModels>, TTableName>>
  >
  from<Row = Record<string, unknown>, Insert = Partial<Row>, Update = Partial<Insert>>(
    table: string,
    options?: AthenaFromOptions,
  ): V3TableBuilder<Row, Insert, Update>
  rpc<Row = unknown, Args extends AthenaJsonObject = AthenaJsonObject>(
    fn: string,
    args?: Args,
    options?: AthenaRpcCallOptions,
  ): RpcQueryBuilder<Row>
  query<Row = unknown>(
    query: string,
    options?: AthenaGatewayCallOptions,
  ): Promise<AthenaResult<Row[]>>
  request<T = unknown>(options: AthenaRequestOptions): Promise<AthenaRequestResponse<T>>
  verifyConnection(
    options?: AthenaGatewayConnectionOptions,
  ): Promise<AthenaGatewayConnectionResult>
  withContext(context: AthenaRequestContext): AthenaClient<TModels>
}

interface ResolvedServiceUrls {
  db?: string
  auth?: string
  chat?: string
  chatWs?: string
  storage?: string
  billing?: string
}

interface AthenaClientCoreBase<TModels extends AthenaClientModelsInput | undefined> {
  readonly config: AthenaClientConfig<TModels>
  readonly urls: ResolvedServiceUrls
  readonly key: string
  readonly client?: string
}

interface AthenaClientCore<TModels extends AthenaClientModelsInput | undefined>
  extends AthenaClientCoreBase<TModels> {
  readonly internalCore: InternalAthenaClientCore<ResolvedModels<TModels>>
}

function readFirstEnv(
  env: Record<string, string | undefined> | undefined,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = env?.[key]?.trim()
    if (value) return value
  }
  return undefined
}

function normalizeOptional(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized || undefined
}

function appendPath(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

function toWebSocketUrl(baseUrl: string): string {
  const url = new URL(baseUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/wss/gateway`
  url.search = ''
  url.hash = ''
  return url.toString()
}

function resolveCore<TModels extends AthenaClientModelsInput | undefined>(
  config: AthenaClientConfig<TModels>,
): AthenaClientCore<TModels> {
  const env = config.env
  const explicitRoot = normalizeOptional(config.url)
  const envRoot = readFirstEnv(env, ENV_URL_KEYS)
  const root = explicitRoot ?? envRoot
  const explicitRootWinsOverEnvServices = explicitRoot !== undefined
  const resolveService = (
    explicit: string | null | undefined,
    envKeys: readonly string[],
    path: string,
  ): string | undefined =>
    normalizeOptional(explicit) ??
    (explicitRootWinsOverEnvServices && root ? appendPath(root, path) : undefined) ??
    readFirstEnv(env, envKeys) ??
    (root ? appendPath(root, path) : undefined)

  const dbUrl = resolveService(config.db?.url, ENV_DB_URL_KEYS, 'db')
  const urls: ResolvedServiceUrls = {
    db: dbUrl,
    auth: resolveService(config.auth?.url, ENV_AUTH_URL_KEYS, 'auth'),
    chat: resolveService(config.chat?.url, ENV_CHAT_URL_KEYS, 'chat'),
    storage: resolveService(config.storage?.url, ENV_STORAGE_URL_KEYS, 'storage'),
    // Billing lives on the main Athena HTTP surface (same host as gateway/db).
    billing:
      normalizeOptional(config.billing?.url) ??
      dbUrl ??
      root ??
      undefined,
    chatWs:
      normalizeOptional(config.chat?.wsUrl) ??
      (explicitRootWinsOverEnvServices && root ? toWebSocketUrl(root) : undefined) ??
      readFirstEnv(env, ENV_CHAT_WS_URL_KEYS) ??
      (root ? toWebSocketUrl(root) : undefined),
  }

  if (!urls.db && !urls.auth && !urls.chat && !urls.storage && !urls.billing) {
    throw new AthenaConfigurationError(
      'ATHENA_NO_SERVICE_CONFIGURED',
      'Athena requires a unified URL or at least one configured service URL.',
      'db',
    )
  }

  const key = normalizeOptional(config.key) ?? readFirstEnv(env, ENV_KEY_KEYS)
  if (!key) {
    throw new AthenaConfigurationError(
      'ATHENA_API_KEY_REQUIRED',
      'Athena API key is required. Pass key or provide it through the explicit env object.',
    )
  }

  const base: AthenaClientCoreBase<TModels> = {
    config,
    urls,
    key,
    client: normalizeOptional(config.client) ?? readFirstEnv(env, ENV_CLIENT_KEYS),
  }
  return {
    ...base,
    internalCore: createInternalClientCore(createInternalConfig(base)),
  }
}

async function resolveConfiguredContext<TModels extends AthenaClientModelsInput | undefined>(
  core: AthenaClientCore<TModels>,
  viewContext: AthenaRequestContext | undefined,
): Promise<AthenaRequestContext | undefined> {
  const configured = core.config.context
  const base = typeof configured === 'function' ? await configured() : configured
  return mergeAthenaRequestContexts(base, viewContext)
}

function createInternalConfig<TModels extends AthenaClientModelsInput | undefined>(
  core: AthenaClientCoreBase<TModels>,
) {
  const { config, urls } = core
  const { url: _authUrl, ...authOptions } = config.auth ?? {}
  const { url: _storageUrl, directUpload, ...storageOptions } = config.storage ?? {}
  void _authUrl
  void _storageUrl

  return {
    baseUrl: urls.db ?? MISSING_DB_SENTINEL,
    apiKey: core.key,
    client: core.client,
    backend:
      typeof config.backend === 'string'
        ? { type: config.backend }
        : config.backend,
    headers: config.headers,
    authUrl: urls.auth,
    chatUrl: urls.chat,
    chatWsUrl: urls.chatWs,
    storageUrl: urls.storage,
    pgUri: config.db?.pgUri,
    jdbcUrl: config.db?.jdbcUrl,
    auth: authOptions,
    models: config.models as ResolvedModels<TModels>,
    behavior: {
      retryReads: config.retryReads,
      traceQueries: config.traceQueries,
      debugAst: config.debugAst,
      findManyAst: config.findManyAst,
    },
    storage: {
      ...storageOptions,
      ...(directUpload ? { directUpload } : {}),
    },
  }
}

function serviceGuard(urls: ResolvedServiceUrls, service: AthenaService): void {
  if (!urls[service]) {
    throw new AthenaConfigurationError(
      'ATHENA_SERVICE_NOT_CONFIGURED',
      `Athena ${service} is not configured.`,
      service,
    )
  }
}

function createUnavailableNamespace(
  service: AthenaService,
  urls: ResolvedServiceUrls,
  path: readonly PropertyKey[] = [],
): unknown {
  return new Proxy(() => undefined, {
    get(_target, property) {
      return createUnavailableNamespace(service, urls, [...path, property])
    },
    apply() {
      serviceGuard(urls, service)
      throw new TypeError(`Athena service property ${path.map(String).join('.')} is not callable.`)
    },
  })
}

function createClientView<TModels extends AthenaClientModelsInput | undefined>(
  core: AthenaClientCore<TModels>,
  viewContext?: AthenaRequestContext,
): AthenaClient<TModels> {
  const resolved: InternalAthenaClient<ResolvedModels<TModels>> =
    createInternalClientView(core.internalCore, () =>
      resolveConfiguredContext(core, viewContext),
    )
  const requireDb = (): void => serviceGuard(core.urls, 'db')

  const { url: _billingUrl, ...billingOptions } = core.config.billing ?? {}
  void _billingUrl
  const billingModule: AthenaBillingModule | unknown = core.urls.billing
    ? createBillingModule({
        baseUrl: core.urls.billing,
        apiKey: core.key,
        client: core.client,
        headers: {
          ...(core.config.headers ?? {}),
          ...(billingOptions.headers ?? {}),
        },
        ...billingOptions,
      })
    : createUnavailableNamespace('billing', core.urls)

  const client: AthenaClient<TModels> = {
    db: (core.urls.db ? resolved.db : createUnavailableNamespace('db', core.urls)) as AthenaClient<TModels>['db'],
    auth: (core.urls.auth ? resolved.auth : createUnavailableNamespace('auth', core.urls)) as AthenaAuthBindings,
    chat: (core.urls.chat ? resolved.chat : createUnavailableNamespace('chat', core.urls)) as AthenaChatModule,
    storage: (core.urls.storage
      ? resolved.storage
      : createUnavailableNamespace('storage', core.urls)) as AthenaStorageModule,
    billing: billingModule as AthenaBillingModule,
    from: ((...args: unknown[]) => {
      requireDb()
      return Reflect.apply(resolved.from, resolved, args)
    }) as AthenaClient<TModels>['from'],
    rpc: ((...args: unknown[]) => {
      requireDb()
      return Reflect.apply(resolved.rpc, resolved, args)
    }) as AthenaClient<TModels>['rpc'],
    query<Row = unknown>(query: string, options?: AthenaGatewayCallOptions) {
      requireDb()
      return resolved.query<Row>(query, options)
    },
    request<T = unknown>(options: AthenaRequestOptions) {
      // Absolute URLs do not need a configured service base URL.
      if (!options.url?.trim()) {
        serviceGuard(core.urls, options.service ?? 'db')
      }
      return resolved.request<T>(options)
    },
    verifyConnection(options?: AthenaGatewayConnectionOptions) {
      requireDb()
      return resolved.verifyConnection(options)
    },
    withContext(context: AthenaRequestContext) {
      return createClientView(core, mergeAthenaRequestContexts(viewContext, context))
    },
  }

  return Object.freeze(client)
}

export function createClient<
  const TModels extends AthenaClientModelsInput | undefined = undefined,
>(config: AthenaClientConfig<TModels>): AthenaClient<TModels> {
  return createClientView(resolveCore(config))
}
